"use client";

// FILE: src/features/nmck-calculator/hooks/use-nmtck-preview.ts
// PURPOSE: Клиентский хук для предварительного расчёта НМЦК без вызова RPC.
// Все формулы дублируют логику calculate_nmtck RPC на фронте.

import { useMemo } from "react";

// START_MODULE_CONTRACT:
// PURPOSE: Provides a client-side preview calculation of NMCC, duplicating the calculate_nmtck RPC logic on the frontend.
// SCOPE: Pure calculation functions plus a React hook wrapping them with useMemo.
// INPUT: NmtckPreviewParams — all parameters needed for the calculation.
// OUTPUT: NmtckPreviewResult — full breakdown of the calculation.
// KEYWORDS: DOMAIN(NMCC): PreviewCalculation; TECH(React): ClientHook; MATH(Formula): NMCC
// END_MODULE_CONTRACT

// START_CHANGE_SUMMARY:
// LAST_CHANGE: 1.1.0 — Добавлен calculateUBase (CASE-формула RPC v4) для синхронизации превью и сервера.
// PREV_CHANGE_SUMMARY: 1.0.0 — Initial implementation of preview calculation hook.
// END_CHANGE_SUMMARY

// <MEMENTO>2026-08-21: calculateUBase дублирует CASE-ветку calculate_nmtck RPC v4
// (supabase/migrations/20260617000000_nmck_rpc_v4_fix.sql): >=24 → 1.0; =12 → 1.5;
// >=3 → 2.0 − 0.0417×h; иначе 1.0. Ранее превью хардкодило uBase=1.0 (расхождение
// превью↔RPC найдено разведкой Ф4): 12-часовой пост показывал в превью 1.0, а RPC
// считал 1.5. Формула 2.0 − 0.0417×h НЕ имеет нижней границы — для h в [3,24) она
// даёт (1.875 … 1.041), совпадает с RPC ровно. НЕ добавлять Math.max-флор: RPC его не имеет.</MEMENTO>
export function calculateUBase(scheduleHoursPerDay: number): number {
  if (scheduleHoursPerDay >= 24) return 1.0;
  if (scheduleHoursPerDay === 12) return 1.5;
  if (scheduleHoursPerDay >= 3) return 2.0 - 0.0417 * scheduleHoursPerDay;
  return 1.0;
}

export interface NmtckPreviewParams {
  mrot: number;
  snr: number;
  regionalCoef: number;
  northCoef: number;
  totalHours: number;
  nightHours: number;
  holidayHours: number;
  uBase: number;
  uDopSum: number;
  insuranceRate: number;
  indirectRate: number;
  profitRate: number;
  postsCount: number;
  stsoAmount: number;
  insuranceAmount: number;
  inflationIndex: number;
  vatPercent: number;
}

export interface NmtckPreviewResult {
  bzp: number;
  adjustedHour: number;
  nightAdditionPerHour: number;
  holidayAdditionPerHour: number;
  vacationReservePerHour: number;
  fotPerHour: number;
  svPerHour: number;
  uTotal: number;
  directPerHour: number;
  indirectPerHour: number;
  profitPerHour: number;
  svohrPerHour: number;
  fotTotal: number;
  svohrTotal: number;
  nmtckPreVat: number;
  vatAmount: number;
  nmtckTotal: number;
}

// START_FUNCTION_calculateNmtckPreview
// START_CONTRACT:
// PURPOSE: Pure calculation function that duplicates server-side RPC logic for NMCC preview.
// INPUTS:
// - params: NmtckPreviewParams — all required inputs for the calculation
// OUTPUTS:
// - NmtckPreviewResult — full breakdown with hourly rates, taxes, totals
// COMPLEXITY_SCORE: 6
// END_CONTRACT
export function calculateNmtckPreview(params: NmtckPreviewParams): NmtckPreviewResult {
  const bzp = params.mrot / params.snr;
  const adjustedHour = bzp * params.regionalCoef * (1 + params.northCoef / 100);
  const nightRatio = params.totalHours > 0 ? params.nightHours / params.totalHours : 0;
  const holidayRatio = params.totalHours > 0 ? params.holidayHours / params.totalHours : 0;
  const nightAdditionPerHour = 0.20 * adjustedHour * nightRatio;
  const holidayAdditionPerHour = 1.00 * adjustedHour * holidayRatio;
  const fotBeforeVacation = adjustedHour + nightAdditionPerHour + holidayAdditionPerHour;
  const vacationReservePerHour = fotBeforeVacation / 12;
  const fotPerHour = fotBeforeVacation + vacationReservePerHour;
  const svPerHour = fotPerHour * params.insuranceRate;
  const uTotal = params.uBase + params.uDopSum;
  const directPerHour = (fotPerHour + svPerHour) * uTotal;
  const indirectPerHour = directPerHour * params.indirectRate;
  const profitPerHour = (directPerHour + indirectPerHour) * params.profitRate;
  const svohrPerHour = directPerHour + indirectPerHour + profitPerHour;

  const fotTotal = fotPerHour * params.totalHours * params.postsCount;
  const svohrTotal = svohrPerHour * params.totalHours * params.postsCount;
  const preInflation = svohrTotal + params.stsoAmount + params.insuranceAmount;
  const nmtckPreVat = preInflation * params.inflationIndex;
  const vatAmount = nmtckPreVat * (params.vatPercent / 100);
  const nmtckTotal = nmtckPreVat + vatAmount;

  return {
    bzp, adjustedHour, nightAdditionPerHour, holidayAdditionPerHour,
    vacationReservePerHour, fotPerHour, svPerHour, uTotal,
    directPerHour, indirectPerHour, profitPerHour, svohrPerHour,
    fotTotal, svohrTotal, nmtckPreVat, vatAmount, nmtckTotal,
  };
}
// END_FUNCTION_calculateNmtckPreview

// START_FUNCTION_useNmtckPreview
// START_CONTRACT:
// PURPOSE: React hook that wraps calculateNmtckPreview with useMemo for reactive preview calculation.
// INPUTS:
// - params: NmtckPreviewParams — reactive parameters
// OUTPUTS:
// - NmtckPreviewResult | null — null when mrot or totalHours = 0
// COMPLEXITY_SCORE: 2
// END_CONTRACT
export function useNmtckPreview(params: NmtckPreviewParams): NmtckPreviewResult | null {
  const { mrot, totalHours, snr, regionalCoef, northCoef, nightHours, holidayHours, uBase, uDopSum, insuranceRate, indirectRate, profitRate, postsCount, stsoAmount, insuranceAmount, inflationIndex, vatPercent } = params;

  return useMemo(() => {
    if (mrot <= 0 || totalHours <= 0) return null;
    return calculateNmtckPreview({ mrot, totalHours, snr, regionalCoef, northCoef, nightHours, holidayHours, uBase, uDopSum, insuranceRate, indirectRate, profitRate, postsCount, stsoAmount, insuranceAmount, inflationIndex, vatPercent });
  }, [mrot, totalHours, snr, regionalCoef, northCoef, nightHours, holidayHours, uBase, uDopSum, insuranceRate, indirectRate, profitRate, postsCount, stsoAmount, insuranceAmount, inflationIndex, vatPercent]);
}
// END_FUNCTION_useNmtckPreview
